import cn from 'classnames';
import { FC, MouseEvent } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Icon } from '@/shared/ui';

import {
  ConsultationStatus,
  formatConsultationDuration,
  type TranscriptorConsultation,
  transcriptorMessages,
} from '@/entities/transcriptor';

import styles from './PatientRecordingsItem.module.scss';

type PatientRecordingsItemProps = {
  consultation: TranscriptorConsultation;
  userTimezone: string;
  isStale: boolean;
  className?: string;
  onClick: (consultation: TranscriptorConsultation) => void;
  onForceStop: (consultationID: string) => void;
};

export const PatientRecordingsItem: FC<PatientRecordingsItemProps> = ({
  consultation,
  userTimezone,
  isStale,
  className,
  onClick,
  onForceStop,
}) => {
  const { formatDate } = useIntl();

  const { name, schemaName, status, date, audioDuration } = consultation;

  const isClickable = status !== 'created';

  const meta = [
    formatDate(date, {
      dateStyle: 'medium',
      timeStyle: 'short',
      timeZone: userTimezone,
    }),
    schemaName,
    formatConsultationDuration(audioDuration),
  ]
    .filter(Boolean)
    .join(' · ');

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    // The whole card opens the recordings page; a row opens its recording.
    event.stopPropagation();
    onClick(consultation);
  };

  return (
    <div
      data-component="patient-recordings-item"
      className={cn(
        styles.container,
        !isClickable && styles.notClickable,
        className,
      )}
      onClick={handleClick}
    >
      <Icon name="scribe" size={22} className={styles.icon} />

      <div className={styles.content}>
        <h5 className={cn('h6', styles.title)}>
          {name ?? schemaName ?? (
            <FormattedMessage
              {...transcriptorMessages.consultationTypeDental}
            />
          )}
        </h5>

        <p className={cn('p2', styles.meta)}>{meta}</p>

        <ConsultationStatus
          consultation={consultation}
          isStale={isStale}
          onForceStop={onForceStop}
          className={styles.status}
        />
      </div>
    </div>
  );
};
